import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from './useMultiTenant';
import { toast } from 'sonner';
import type { PriceTable, PriceTableFormData, ProductPrice } from '@/types/priceTable';

export function usePriceTables() {
  const { companyId } = useMultiTenant();
  const queryClient = useQueryClient();

  // Buscar todas as tabelas de preço
  const { data: priceTables = [], isLoading } = useQuery({
    queryKey: ['price-tables', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('price_tables')
        .select('*')
        .eq('company_id', companyId)
        .eq('active', true)
        .order('display_order')
        .order('name');

      if (error) throw error;
      // PRECO-PADRAO (I.160): a "Preço Padrão" sempre primeiro
      return [...((data ?? []) as PriceTable[])].sort(
        (a, b) => Number(!!b.is_default) - Number(!!a.is_default),
      );
    },
    enabled: !!companyId,
  });

  // Criar tabela de preços
  const createTable = useMutation({
    mutationFn: async (data: PriceTableFormData) => {
      const { data: table, error } = await supabase
        .from('price_tables')
        .insert({
          company_id: companyId,
          ...data,
        })
        .select()
        .single();

      if (error) throw error;
      return table;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] });
      toast.success('Tabela de preços criada com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao criar tabela:', error);
      const pgError = error as { code?: string; message?: string };
      if (
        pgError.code === 'P0001' ||
        pgError.message?.includes('limite de 5 tabelas')
      ) {
        toast.error(
          'Limite de 5 tabelas de preço da empresa atingido (além da Preço Padrão). Exclua uma tabela existente antes de criar uma nova.'
        );
      } else {
        toast.error('Erro ao criar tabela de preços');
      }
    },
  });

  // Buscar preços de um produto em todas as tabelas
  const fetchProductPrices = async (productId: string): Promise<ProductPrice[]> => {
    const { data, error } = await supabase
      .from('product_prices')
      .select('*, price_table:price_tables(*)')
      .eq('product_id', productId)
      .eq('company_id', companyId)
      .eq('active', true);

    if (error) throw error;
    return data as ProductPrice[];
  };

  // Salvar preço de produto em uma tabela específica
  // PRECO-PADRAO (I.160): grava só a coluna informada (antes mandava as duas e trocava vazio por 0 na outra)
  const saveProductPrice = useMutation({
    mutationFn: async ({
      productId,
      priceTableId,
      salePrice,
      rentalPrice,
    }: {
      productId: string;
      priceTableId: string;
      salePrice?: number | null;
      rentalPrice?: number | null;
    }) => {
      const row: Record<string, unknown> = {
        company_id: companyId,
        product_id: productId,
        price_table_id: priceTableId,
      };
      if (salePrice !== undefined) row.sale_table_price = salePrice;
      if (rentalPrice !== undefined) row.rental_table_price = rentalPrice;
      const { data, error } = await supabase
        .from('product_prices')
        .upsert(row as any, { onConflict: 'product_id,price_table_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-prices'] });
      queryClient.invalidateQueries({ queryKey: ['price-tables'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Preço atualizado com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao salvar preço:', error);
      toast.error('Erro ao salvar preço');
    },
  });

  // Deletar tabela de preços
  const deleteTable = useMutation({
    mutationFn: async (tableId: string) => {
      const { error } = await supabase
        .from('price_tables')
        .update({ active: false })
        .eq('id', tableId)
        .eq('company_id', companyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] });
      toast.success('Tabela removida com sucesso!');
    },
    onError: (error) => {
      console.error('Erro ao remover tabela:', error);
      toast.error('Erro ao remover tabela');
    },
  });

  return {
    priceTables,
    isLoading,
    createTable,
    deleteTable,
    fetchProductPrices,
    saveProductPrice,
  };
}
